import { isSafari, isIE, isIOS, isChrome, isEdge, isMobile, isTablet } from 'react-device-detect';
import { SessionStatus, isAfterSessionStatus } from './SessionStatus';
import EmbeddedRoles from './VideoMeetings';

export const Vendor = Object.freeze({
  AMAZON_IVS: 'Amazon IVS',
  BRIGHTCOVE: 'Brightcove',
  CVP: 'Cvent Video Player',
  VIMEO: 'Vimeo',
  WEBLINK: 'Weblink',
  ZOOM: 'Zoom',
  CVENT_VOD: 'Cvent VOD',
  EMBEDDED_CVC: 'Embedded Cvent Video Conferencing'
});

// VendorType is a union of all the values of the Vendor object
export type VendorType = (typeof Vendor)[keyof typeof Vendor];

export const SolutionType = Object.freeze({
  CVC: 'Cvent Video Conferencing',
  EXTERNAL: 'External Platform',
  CVENT_STUDIO: 'Cvent Studio',
  CVENT_STUDIO_LITE: 'Cvent Studio Lite',
  EMBEDDED_CVENT_STUDIO: 'Embedded Cvent Studio',
  EMBEDDED_CVC: 'Embedded Cvent Video Conferencing'
});

export const WebcastFormat = Object.freeze({
  LIVE: 'Live',
  PRE_RECORDED: 'Pre_recorded'
});

export const WebcastType = Object.freeze({
  MEETING: 'Meeting',
  WEBINAR: 'Webinar'
});

const EMBEDDED_VENDORS = new Set<VendorType>([
  Vendor.AMAZON_IVS,
  Vendor.BRIGHTCOVE,
  Vendor.CVENT_VOD,
  Vendor.CVP,
  Vendor.VIMEO,
  Vendor.EMBEDDED_CVC
]);

const ATTENDANCE_TRACKING_VENDORS = new Set<VendorType>([
  Vendor.AMAZON_IVS,
  Vendor.BRIGHTCOVE,
  Vendor.CVENT_VOD,
  Vendor.CVP,
  Vendor.VIMEO,
  Vendor.EMBEDDED_CVC
]);

export const LIVE_STATUS_VENDORS = new Set<VendorType>([Vendor.AMAZON_IVS, Vendor.CVP, Vendor.CVENT_VOD]);

export const ACTIVE_STREAM_STATES = new Set(['Disconnected', 'Processing', 'Waiting']);

export const TERMINAL_STREAM_STATES = new Set(['Cancelled', 'Cancelling', 'Failed', 'Finished', 'Finishing']);

export const REACTIONS_VENDORS = new Set<VendorType>([
  Vendor.EMBEDDED_CVC,
  Vendor.AMAZON_IVS,
  Vendor.CVP,
  Vendor.CVENT_VOD
]);

/**
 * Determines whether an alert should be presented to the attendee
 * indicating to use a supported browser/device for a Zoom embedded experience
 * @param {Vendor} vendor
 * @param {SessionStatus} sessionStatus
 * @param {boolean} isZoomEnabled
 */
export const showZoomAlert = (vendor, sessionStatus, isZoomEnabled) => {
  return (
    isZoomEnabled &&
    vendor === Vendor.ZOOM &&
    [SessionStatus.DURING, SessionStatus.LESS_THAN_FIVE_MIN_BEFORE].includes(sessionStatus) &&
    (isSafari || isIE || isIOS)
  );
};

export const isZoomEmbedded = (vendor, sessionStatus) => {
  return vendor === Vendor.ZOOM && sessionStatus !== SessionStatus.AFTER && !(isSafari || isIE || isIOS);
};

export const isStudio = solutionType => {
  return [SolutionType.CVENT_STUDIO, SolutionType.CVENT_STUDIO_LITE, SolutionType.EMBEDDED_CVENT_STUDIO].includes(
    solutionType
  );
};

/*
 *  Mobile/Tablet does not have hardware requirement & face issues while operating studio in these devices
 */
export const studioInvalidDevices = () => isMobile || isTablet;

export const showStudioAlert = solutionType => {
  const isNonChromeNonEdge = !isChrome && !isEdge;
  const isMobileOrTablet = isMobile || isTablet;
  return isStudio(solutionType) && (isNonChromeNonEdge || isMobileOrTablet);
};

export const isCventStudioEmbed = solutionType => {
  return [SolutionType.CVENT_STUDIO_LITE, SolutionType.EMBEDDED_CVENT_STUDIO].includes(solutionType);
};

export const getBroadcastType = solutionType => {
  let broadcastType = '';
  switch (solutionType) {
    case SolutionType.CVENT_STUDIO:
      broadcastType = 'diy';
      break;
    case SolutionType.CVENT_STUDIO_LITE:
      broadcastType = 'webinar';
      break;
    case SolutionType.EMBEDDED_CVENT_STUDIO:
      broadcastType = 'DIY_Embedded';
      break;
    default:
      break;
  }
  return broadcastType;
};

export const determineVendor = (webcast, sessionStatus, isZoomEmbedEnabled = false, joinEmbedRole = null) => {
  const webcastPlayer = webcast?.player || {};

  // Our vendor is now stored in the webcast's 'playerType' property
  let vendor = webcast?.playerType;

  const isCollaborative = webcast?.format === WebcastFormat.LIVE && webcast?.type === WebcastType.MEETING;

  /*
   * WEBINAR-2317 - In case of recording for interactive embedded sessions, player type provider is Amazon IVS
   * This condition has been moved to the top to ensure that vendor is assigned as embedded CVC in this case
   * Changing the order of this first condition might introduce bugs
   */
  if (isCollaborative && webcast?.solutionType === SolutionType.EMBEDDED_CVC) {
    if (isAfterSessionStatus(sessionStatus) && webcast?.onDemandVideo && !isHostOrSpeakerRole(joinEmbedRole)) {
      vendor = Vendor.CVENT_VOD;
    } else {
      vendor = Vendor.EMBEDDED_CVC;
    }
  } else if (
    vendor === Vendor.CVP &&
    !!webcastPlayer?.playerTypeProvider &&
    [Vendor.AMAZON_IVS, Vendor.CVENT_VOD].includes(webcastPlayer.playerTypeProvider)
  ) {
    // Override vendor of CVP since we will use the player type provider for known cases
    vendor = webcastPlayer.playerTypeProvider;
  } else if (
    isCollaborative &&
    webcast?.solutionType === SolutionType.CVC &&
    isAfterSessionStatus(sessionStatus) &&
    !!webcastPlayer?.playerTypeProvider &&
    [Vendor.BRIGHTCOVE, Vendor.CVENT_VOD].includes(webcastPlayer.playerTypeProvider)
  ) {
    // Use player type provider as vendor for collaborative sessions in after state
    if (Vendor.BRIGHTCOVE === webcastPlayer.playerTypeProvider) {
      vendor = Vendor.CVP;
    } else {
      vendor = Vendor.CVENT_VOD;
    }
  } else if (
    isZoomEmbedEnabled &&
    isCollaborative &&
    (webcast?.solutionType === SolutionType.CVC ||
      (webcast?.solutionType === SolutionType.EXTERNAL && webcast?.provider === Vendor.ZOOM))
  ) {
    // Override vendor to allow for testing Zoom embed w/o a dedicated player type
    vendor = Vendor.ZOOM;
  }

  return vendor;
};

export const shouldEnableAttendanceTracking = vendor => {
  return ATTENDANCE_TRACKING_VENDORS.has(vendor);
};

/**
 * Indicates whether the session video for the given vendor is embedded or not.
 * @param {Vendor | string} vendor
 * @param {SessionStatus | string} sessionStatus
 * @param {boolean} isZoomEnabled
 */
const isVendorEmbedded = (vendor, sessionStatus, isZoomEnabled) => {
  // TODO WAX-2383 remove isZoomEnabled post-release
  return (
    (vendor !== Vendor.WEBLINK && EMBEDDED_VENDORS.has(vendor)) ||
    (isZoomEnabled && isZoomEmbedded(vendor, sessionStatus))
  );
};

/**
 * Indicates whether embedded CVC session is joined as host or speaker
 */
const isHostOrSpeakerRole = joinEmbedRole => {
  return joinEmbedRole && (joinEmbedRole === EmbeddedRoles.HOST || joinEmbedRole === EmbeddedRoles.SPEAKER);
};

export default isVendorEmbedded;
